import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, Download, ExternalLink, FileText } from "lucide-react";

/**
 * Modal that displays a PDF using the browser's native PDF viewer (iframe).
 * Mobile Safari sometimes blocks inline PDF rendering — in that case the user
 * sees the "Open / Download" button instead, which works on every device.
 */
export function BannerPdfDrawer({ pdfUrl, title, open, onClose }) {
  if (!pdfUrl) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-5xl w-[95vw] h-[90vh] p-0 gap-0 flex flex-col"
        data-testid="banner-pdf-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-white">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-5 h-5 text-stone-500 flex-shrink-0" />
            <span className="font-medium text-sm truncate">{title || "Document"}</span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-stone-600 hover:bg-stone-100 rounded transition-colors"
              data-testid="banner-pdf-open-new-tab"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open
            </a>
            <a
              href={pdfUrl}
              download
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-stone-600 hover:bg-stone-100 rounded transition-colors"
              data-testid="banner-pdf-download"
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </a>
            <button
              onClick={onClose}
              className="inline-flex items-center justify-center w-7 h-7 hover:bg-stone-100 rounded transition-colors ml-1"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* PDF viewer */}
        <div className="flex-1 bg-stone-100 overflow-hidden">
          <iframe
            src={`${pdfUrl}#toolbar=1&navpanes=0`}
            title={title || "PDF document"}
            className="w-full h-full border-0"
            data-testid="banner-pdf-iframe"
          />
        </div>

        {/* Fallback message — only visible if iframe fails to render (rare,
            mostly mobile Safari). Position absolute behind the iframe so it
            only shows through when the iframe content is empty. */}
        <noscript>
          <div className="p-6 text-center text-sm text-stone-600">
            Your browser cannot display this PDF inline.
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline ml-1">Open it in a new tab</a>.
          </div>
        </noscript>
      </DialogContent>
    </Dialog>
  );
}

export default BannerPdfDrawer;
